import type { EventView } from "~/business-areas/event/composables/event-view.hook";
import { useGrowthContactFormHistory } from "~/business-areas/growth/composables/growth-contact-form-history.hook";
import type { ContactFlowMetadata } from "~~/model/analytics.model";

export const POST_VIEW_REPLAY_REQUEST_QUERY_PARAMETER_KEY = "view_replay";
export const AUTO_REPLAY_OPENING_DELAY_IN_MS = 600;

export function useEventReplayCta(event: EventView) {
  const router = useRouter();

  const bindings = computed<
    ContactFlowMetadata & { redirect: string; confirmationToast: boolean }
  >(() => {
    return {
      origin: "events",
      origin_page_action: "view_replay_cta",
      origin_page_id: event.slug,
      redirect: `${router.resolve(event._links.page).path}?${POST_VIEW_REPLAY_REQUEST_QUERY_PARAMETER_KEY}=true`,
      confirmationToast: false,
    };
  });

  return { bindings };
}

export function useEventReplay(
  event: Pick<EventView, "replay" | "_links" | "slug"> & {
    course: Pick<EventView["course"], "title">;
  },
) {
  const router = useRouter();
  const route = useRoute();
  const { hasCompletePII, state } = useGrowthContact();
  const { exists } = useGrowthContactFormHistory();

  const ctaBindings = computed<
    ContactFlowMetadata & { redirect: string; confirmationToast: boolean }
  >(() => {
    return {
      origin: "events",
      origin_page_action: "view_replay_cta",
      origin_page_id: event.slug,
      redirect: `${router.resolve(event._links.page).path}?${POST_VIEW_REPLAY_REQUEST_QUERY_PARAMETER_KEY}=true`,
      confirmationToast: false,
    };
  });

  function view() {
    if (!event.replay) return;

    useVideoViewer().open({
      title: event.course.title,
      playback: event.replay.playback,
    });

    return navigateTo({
      query: {
        ...route.query,
        [POST_VIEW_REPLAY_REQUEST_QUERY_PARAMETER_KEY]: undefined,
      },
    });
  }

  const hasToFillContactFormBeforeReplay = computed(() => {
    return (
      !hasCompletePII.value ||
      !exists({
        email: state.value?.email ?? "",
        origin: "events",
        origin_page_action: "view_replay_cta",
        origin_page_id: event.slug,
      })
    );
  });

  return {
    view,
    ctaBindings,
    hasToFillContactFormBeforeReplay,
  };
}
